<template>
    <transition name="fade">
        <div v-if="modelValue" class="fixed inset-0 z-50 flex flex-col bg-white">

            <!-- Header -->
            <div class="flex justify-between items-center p-4 border-b shadow-md bg-[#D7642A] text-white">
                <h2 class="text-2xl font-bold">
                    {{ mode === 'edit' ? 'Edit Article' : 'Create Article' }}
                </h2>
                <button @click="closeModal" class="text-white hover:text-gray-100 text-2xl font-bold">✕</button>
            </div>

            <!-- Content -->
            <div class="flex flex-col md:flex-row flex-1 overflow-hidden">

                <!-- LEFT: Inputs -->
                <div class="md:w-1/2 overflow-y-auto p-6 space-y-6">

                    <!-- Title -->
                    <div>
                        <label class="block font-semibold mb-1 text-gray-700">Article Title:</label>
                        <input v-model="articleTitle" type="text" placeholder="Enter article title"
                            class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
                    </div>

                    <!-- Status Buttons -->
                    <div>
                        <label class="block font-semibold mb-1 text-gray-700">Status:</label>
                        <div class="flex gap-2 flex-wrap">

                            <button type="button" @click="status = 'draft'"
                                class="px-4 py-2 rounded border text-sm font-medium transition"
                                :class="status === 'draft' ? 'bg-gray-300 text-gray-800 border-gray-300' : 'bg-white text-gray-600 hover:bg-gray-100'">
                                Draft
                            </button>

                            <button type="button" @click="status = 'published'"
                                class="px-4 py-2 rounded border text-sm font-medium transition"
                                :class="status === 'published' ? 'bg-green-200 text-green-800 border-green-200' : 'bg-white text-gray-600 hover:bg-gray-100'">
                                Published
                            </button>

                            <button type="button" @click="status = 'archived'"
                                class="px-4 py-2 rounded border text-sm font-medium transition"
                                :class="status === 'archived' ? 'bg-yellow-200 text-yellow-900 border-yellow-200' : 'bg-white text-gray-600 hover:bg-gray-100'">
                                Archived
                            </button>

                            <!-- FOR SUPER ROLE ONLY -->
                            <button v-if="isSuper" type="button" @click="status = 'headlines'"
                                class="px-4 py-2 rounded border text-sm font-medium transition"
                                :class="status === 'headlines' ? 'bg-orange-200 text-orange-900 border-orange-200' : 'bg-white text-gray-600 hover:bg-gray-100'">
                                Headlines
                            </button>

                        </div>
                    </div>

                    <!-- Highlight ( FOR SUPER ROLE ONLY )  -->
                    <div v-if="isSuper && status !== 'headlines'" class="flex items-center gap-4">

                        <span class="font-semibold text-gray-700">Highlight Article:</span>

                        <label class="relative inline-flex items-center cursor-pointer">

                            <input type="checkbox" v-model="highlight" class="sr-only peer" />

                            <!-- Track stays gray -->
                            <div
                                class="w-11 h-6 bg-gray-200 rounded-full peer-focus:ring-2 peer-focus:ring-[#F48921] transition-colors">
                            </div>

                            <!-- Thumb moves and changes color -->
                            <div class="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow
                             peer-checked:translate-x-full peer-checked:bg-[#F48921] transition-transform"></div>

                        </label>

                    </div>

                    <!-- Publish Date -->
                    <div>
                        <label class="block font-semibold mb-1 text-gray-700">
                            Publish Date (Optional):
                        </label>

                        <input type="date" v-model="publish_by"
                            class="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F48921] w-60" />

                        <p class="text-xs text-gray-500 mt-1">
                            Leave empty to publish immediately when status is set to Published.
                        </p>
                    </div>

                    <!-- Image Upload -->
                    <div>
                        <label class="block font-semibold mb-1 text-gray-700">Upload Article Image:</label>
                        <div class="relative w-full h-48 border-2 border-dashed border-gray-300 rounded overflow-hidden cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                            @click="!articleImage && fileInput?.click?.()">

                            <div v-if="!articleImage"
                                class="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                                <svg class="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1 M12 12V3m0 0l-3 3m3-3l3 3" />
                                </svg>
                                <span class="text-sm">Click to upload</span>
                            </div>

                            <img v-if="articleImage" :src="articleImage"
                                class="w-full h-full object-contain transition-transform duration-200 hover:scale-105" />

                            <div v-if="articleImage"
                                class="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition">
                                <div class="flex gap-3 flex-wrap">
                                    <button @click.stop="fileInput.click()"
                                        class="bg-white px-3 py-2 rounded shadow text-sm hover:bg-gray-100 flex items-center gap-2">
                                        Replace
                                    </button>
                                    <button @click.stop="removeImage"
                                        class="bg-red-500 text-white px-3 py-2 rounded shadow text-sm hover:bg-red-600 flex items-center gap-2">
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                        <input ref="fileInput" type="file" class="hidden" accept="image/*" @change="onFileChange" />
                    </div>

                    <!-- Editor -->
                    <div>
                        <label class="block font-semibold mb-1 text-gray-700">Article Content:</label>
                        <PrimeEditor v-model="articleContent" editorStyle="height: 240px">
                            <template v-slot:toolbar>
                                <EditorToolbar />
                            </template>
                        </PrimeEditor>
                    </div>

                    <!-- Mobile Preview Button -->
                    <div class="md:hidden mt-4">
                        <button @click="showMobilePreview = true"
                            class="w-full bg-[#F48921] text-white px-4 py-2 rounded shadow hover:bg-[#D7642A] transition">
                            Show Preview
                        </button>
                    </div>

                    <!-- SUBMIT / CANCEL -->
                    <div class="mt-4 flex justify-end gap-2">
                        <button @click="saveArticle" :disabled="loading"
                            class="bg-[#F48921] text-white px-4 py-2 rounded shadow hover:bg-[#D7642A] flex items-center gap-2 transition">
                            <!-- Spinner -->
                            <svg v-if="loading" class="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4" />
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            <span>{{ buttonText }}</span>
                        </button>

                        <button @click="closeModal" :disabled="loading"
                            class="bg-gray-200 text-gray-800 px-4 py-2 rounded shadow hover:bg-gray-300 transition">
                            Cancel
                        </button>
                    </div>

                </div>

                <!-- RIGHT: Live Preview (desktop) -->
                <div class="hidden md:block md:w-1/2 bg-gray-50 p-6 overflow-y-auto rounded-l shadow-inner">
                    <h2 class="text-lg font-semibold border-b pb-2 mb-2 text-gray-700">Live Preview</h2>

                    <div class="flex items-center justify-between mb-2">
                        <span class="text-xl font-bold">{{ articleTitle || 'Article Title' }}</span>
                        <span class="text-xs font-semibold px-3 py-1 rounded-full bg-gray-200 text-gray-800">
                            {{ status.charAt(0).toUpperCase() + status.slice(1) }}
                        </span>
                    </div>

                    <img v-if="articleData?.filename || articleImage"
                        :src="articleImage ? articleImage : `${imageDirectory}${articleData.filename}`"
                        class="w-full max-h-[400px] object-contain rounded mb-2 mx-auto shadow-sm transition-transform hover:scale-105"
                        alt="Article Image" />

                    <div class="ql-snow">
                        <div class="ql-editor" v-html="articleContent"></div>
                    </div>
                </div>

            </div>
        </div>
    </transition>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import PrimeEditor from "primevue/editor";
import EditorToolbar from "@/components/ui/EditorToolbar.vue";
import { useStore } from "vuex";

export default {
    name: "ArticleModal",
    components: { PrimeEditor, EditorToolbar },
    props: {
        modelValue: { type: Boolean, required: true },
        mode: { type: String, default: "create" },
        articleData: { type: Object, default: null }
    },
    emits: ["update:modelValue", "save"],
    setup(props, { emit }) {
        const store = useStore();

        const articleTitle = ref("");
        const articleContent = ref("");
        const articleImage = ref("");
        const status = ref("draft");
        const highlight = ref(false);
        const publish_by = ref("");
        const fileInput = ref(null);
        const showMobilePreview = ref(false);
        const loading = ref(false);

        const imageDirectory = process.env.VUE_APP_GAZETTE_IMG_DIRECTORY;

        const user = computed(() => store.state.auth.user);

        const populateFields = () => {
            if (props.mode === "edit" && props.articleData) {
                articleTitle.value = props.articleData.title || "";
                articleContent.value = props.articleData.content || "";
                articleImage.value = props.articleData.image || "";
                status.value = props.articleData.status || "draft";
                highlight.value = props.articleData.highlight || false;
                publish_by.value = props.articleData.publish_by || "";
            } else {
                articleTitle.value = "";
                articleContent.value = "";
                articleImage.value = "";
                status.value = "draft";
                highlight.value = false;
                publish_by.value = "";
            }
        };

        const buttonText = computed(() => {
            if (loading.value) {
                return props.mode === "edit" ? "Updating..." : "Saving...";
            }
            return props.mode === "edit"
                ? "Update Article"
                : "Save Article";
        });

        watch(
            () => props.modelValue,
            (val) => {
                if (val) populateFields();
            }
        );

        const closeModal = () => {
            if (!loading.value) {
                emit("update:modelValue", false);
            }
        };

        const handleKeydown = (e) => {
            if (e.key === "Escape" && props.modelValue) {
                closeModal();
            }
        };

        onMounted(() => {
            window.addEventListener("keydown", handleKeydown);
        });

        const isSuper = computed(() => user.value?.gazetteRole === "super");

        onBeforeUnmount(() => {
            window.removeEventListener("keydown", handleKeydown);
        });

        const onFileChange = (e) => {
            const file = e.target.files[0];
            if (file) {
                articleImage.value = URL.createObjectURL(file);
            }
        };

        const removeImage = () => {
            articleImage.value = "";
            if (fileInput.value) {
                fileInput.value.value = null;
            }
        };

        const saveArticle = async () => {
            loading.value = true;

            emit("save", {
                id: props.articleData?.id || null,
                title: articleTitle.value,
                content: articleContent.value,
                image: articleImage.value,
                status: status.value,
                highlight: highlight.value ? "true" : "false",
                publish_by: publish_by.value,
                file: fileInput.value?.files[0] || null
            });

            closeModal();

            loading.value = false;
        };

        const statusClass = computed(() => {
            if (status.value === "draft") return "bg-gray-200 text-gray-700";
            if (status.value === "published") return "bg-green-100 text-green-700";
            if (status.value === "archived") return "bg-yellow-100 text-yellow-700";
            if (status.value === "headlines") return "bg-orange-100 text-orange-700";
            return "";
        });

        return {
            articleTitle,
            articleContent,
            articleImage,
            status,
            highlight,
            publish_by,
            fileInput,
            showMobilePreview,
            loading,
            buttonText,
            closeModal,
            onFileChange,
            removeImage,
            saveArticle,
            statusClass,
            imageDirectory,
            user,
            isSuper
        };
    }
};
</script>